import React from 'react';
import { Box, Container, Grid, Typography, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLang } from '../context/LanguageContext';

// 2 videos + 3 images, mapped to the first 5 projects
const projectMedia = [
  { type: 'video', src: '/assets/project-video-1.mp4' },
  { type: 'video', src: '/assets/project-video-2.mp4' },
  { type: 'image', src: '/assets/project-image-1.png' },
  { type: 'image', src: '/assets/project-image-2.png' },
  { type: 'image', src: '/assets/project-image-3.png' },
];

const SectionLabel = ({ children }) => (
  <Box display="flex" alignItems="center" gap={2} mb={2}>
    <Box sx={{ width: 40, height: 3, background: '#C8102E', flexShrink: 0 }} />
    <Typography sx={{
      fontFamily: '"Oswald", sans-serif',
      color: '#C8102E',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      fontSize: '0.78rem',
      fontWeight: 500,
    }}>
      {children}
    </Typography>
  </Box>
);

export default function Portfolio() {
  const { t } = useLang();
  const p = t.portfolio;

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box id="portfolio" sx={{ py: { xs: 7, md: 11 }, background: '#111111' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          mb={{ xs: 4, md: 6 }}
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          gap={3}
        >
          <Box>
            <SectionLabel>{p.eyebrow}</SectionLabel>
            <Typography variant="h2" sx={{ color: '#fff', fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
              {p.heading}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={scrollToContact}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.3)',
              flexShrink: 0,
              '&:hover': { borderColor: '#C8102E', color: '#C8102E', background: 'transparent' },
            }}
          >
            {p.cta}
          </Button>
        </Box>

        {/* Project Grid */}
        <Grid container spacing={2}>
          {projectMedia.map((media, i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Box
                sx={{
                  position: 'relative',
                  height: 260,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover .project-overlay': { opacity: 1 },
                  '&:hover .project-media': { transform: 'scale(1.05)' },
                }}
              >
                {/* Media */}
                {media.type === 'video' ? (
                  <Box
                    component="video"
                    className="project-media"
                    src={media.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    className="project-media"
                    src={media.src}
                    alt={`Project ${i + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      display: 'block',
                    }}
                  />
                )}

                {/* Hover overlay */}
                <Box
                  className="project-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(10,10,10,0.75)',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                    onClick={scrollToContact}
                    sx={{
                      p: 0,
                      color: '#C8102E',
                      fontFamily: '"Oswald", sans-serif',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      '&:hover': { background: 'transparent', color: '#fff' },
                    }}
                  >
                    {p.inquire}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
